import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import api, { formatApiError } from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { Zap, ArrowRight, ShieldCheck, Check } from "lucide-react";
import { toast } from "sonner";

// Coach-defined DUPR skill bands (2.5 → 4.5+), labelled with the canonical 5-tier
// taxonomy used everywhere else in the app: Beginner · Improver · Intermediate ·
// Advanced · Elite. We start the player AT their self-rated level for the coach's
// mandatory on-court assessment.
const LEVEL_OPTIONS = [
  {
    v: "2.5",
    title: "2.5 · Beginner",
    blurb: "Just getting started. You know the rules, the 2-bounce rule and how to keep score, and you're learning to rally with direction. Most shots still go where they want, not where you want.",
  },
  {
    v: "3.0",
    title: "3.0 · Improver",
    blurb: "You play weekly and know the basics, but you're still inconsistent under pressure. Dinks bail mid-rally, third-shot drops are 50/50. Typically fights for points in the lower half of club leagues — exactly where most \"experienced beginners\" sit.",
  },
  {
    v: "3.5",
    title: "3.5 · Intermediate",
    blurb: "Consistent rallies and most strokes land where you intend. Soft game vs hard game distinction emerging, third-shot drops working most of the time, basic stacking understood. Mid-tier league regular.",
  },
  {
    v: "4.0",
    title: "4.0 · Advanced",
    blurb: "Reliable transitions, strong net presence, control over depth and pace. You read opponents, reset under fire, lob when it makes sense. Wins matches against most club opposition.",
  },
  {
    v: "4.5",
    title: "4.5+ · Elite",
    blurb: "Tournament-tested. Power, control and the full advanced toolkit — ATPs, ERNEs, counter-attacks, deliberate strategy. You train for ranking points, not learning.",
  },
];

export default function SelfRating() {
  const { setUser } = useAuth();
  const nav = useNavigate();
  const [selected, setSelected] = useState(null);
  const [dupr, setDupr] = useState("");
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const { data } = await api.get("/me/self-rating");
        if (data?.self_rating?.level) setSelected(data.self_rating.level);
      } catch (e) {
        // soft-fail — first-time players will see no preselection
      }
    })();
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    if (!selected) {
      toast.error("Pick the level that best matches your game");
      return;
    }
    setSaving(true);
    try {
      await api.post("/me/self-rating", { dupr: parseFloat(selected) });
      await api.post("/me/dupr", { dupr_rating: dupr.trim(), skipped: !dupr.trim() }).catch(() => {});
      const me = await api.get("/auth/me");
      setUser(me.data);
      toast.success(`Self-rating set to ${selected}. You'll be assessed at this level.`);
      // Iter79: Direct redirect to /offerings (no personal-details detour).
      // ProtectedRoute still guards missing personal-details; this jump is
      // the "happy path" for a self-rated player who's ready to browse.
      nav("/offerings", { replace: true });
    } catch (e) {
      const status = e?.response?.status;
      const detail = formatApiError(e?.response?.data?.detail);
      const msg = detail && detail !== "Something went wrong."
        ? detail
        : (status ? `Could not save (HTTP ${status}). Please retry — if it keeps happening, contact support.` : "Network error — check your connection and retry.");
      toast.error(msg);
    } finally {
      setSaving(false);
    }
  };

  // Show the player which level they'll actually be assessed at + the target they're working toward.
  const startsAt = (() => {
    if (!selected) return null;
    return selected; // start AT the self-rated level (used to be one below — confusing for strong players)
  })();
  const target = (() => {
    if (!selected) return null;
    const v = parseFloat(selected);
    return v >= 4.5 ? "4.5" : (v + 0.5).toFixed(1);
  })();

  return (
    <div className="min-h-screen bg-[#0A0A0A] text-white noise-bg" data-testid="self-rating-page">
      <div className="max-w-3xl mx-auto px-5 sm:px-6 py-10 sm:py-12">
        <Link to="/" data-testid="self-rating-back-home" className="flex items-center gap-2 mb-8 sm:mb-10 hover:opacity-80 transition-opacity w-fit">
          <div className="w-8 h-8 bg-[#FF4500] flex items-center justify-center"><Zap strokeWidth={2.5} className="w-5 h-5 text-black" /></div>
          <span className="h-display text-xl sm:text-2xl tracking-tighter">RALLY LAB · PICKLEBALL OS</span>
        </Link>

        <div className="label-mono mb-2">STEP 1 OF 2 · SELF-RATING</div>
        <h1 className="h-display text-3xl sm:text-4xl md:text-5xl tracking-tighter mb-3">Where is your pickleball game today?</h1>
        <p className="text-sm text-[#A3A3A3] mb-8 max-w-2xl leading-relaxed">
          Pick the DUPR-style level that best describes you. Your coach will run your first paid assessment <span className="text-white font-semibold">at this exact level</span> — once you tick off every skill at that level, you move up by 0.5 and tackle the next.
        </p>

        <form onSubmit={submit} className="space-y-6">
          <div className="space-y-3" data-testid="level-options">
            {LEVEL_OPTIONS.map((opt) => {
              const isSelected = selected === opt.v;
              return (
                <button
                  key={opt.v}
                  type="button"
                  onClick={() => setSelected(opt.v)}
                  data-testid={`level-option-${opt.v}`}
                  className={`w-full text-left p-5 border transition-all flex items-start gap-4 ${
                    isSelected
                      ? "border-[#FF4500] bg-[#FF4500]/10"
                      : "border-[#262626] hover:border-[#525252] bg-[#0A0A0A]"
                  }`}
                >
                  <div
                    className={`h-display text-3xl sm:text-4xl tracking-tighter shrink-0 w-16 text-center ${
                      isSelected ? "text-[#FF4500]" : "text-[#525252]"
                    }`}
                  >
                    {opt.v}
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="font-semibold mb-1">{opt.title}</div>
                    <div className="text-xs sm:text-sm text-[#A3A3A3] leading-relaxed">{opt.blurb}</div>
                  </div>
                  {isSelected && (
                    <div className="w-7 h-7 rounded-full bg-[#FF4500] flex items-center justify-center shrink-0">
                      <Check strokeWidth={3} className="w-4 h-4 text-black" />
                    </div>
                  )}
                </button>
              );
            })}
          </div>

          {selected && (
            <div className="card-surface p-5 border-l-2 border-[#CCFF00]" data-testid="starting-level-summary">
              <div className="label-mono mb-1" style={{ color: "#CCFF00" }}>YOUR STARTING ASSESSMENT</div>
              <div className="text-sm text-[#E5E5E5]">
                You self-rated <span className="text-white font-semibold">{selected}</span> &mdash; your coach will run your first assessment at level{" "}
                <span className="text-[#CCFF00] h-display text-2xl align-middle ml-1">{startsAt}</span>.
                {target && target !== selected ? <> Tick every skill at that level and you&apos;ll progress to <span className="text-white font-semibold">{target}</span>.</> : <> You&apos;re at the top of the DUPR-style ladder — your coach will help refine your edge.</>}
              </div>
            </div>
          )}

          <label className="block card-surface p-5" data-testid="self-rating-dupr-block">
            <span className="label-mono text-[#A3A3A3]">DUPR RATING (OPTIONAL)</span>
            <span className="block text-xs text-[#737373] mt-1">Have an official DUPR rating? Add it so coaches can see it. Leave blank to skip.</span>
            <input value={dupr} onChange={(e) => setDupr(e.target.value)} maxLength={40} placeholder="e.g. 3.62" data-testid="self-rating-dupr-input"
              className="mt-2 w-full bg-[#141414] border border-[#262626] focus:border-[#CCFF00] focus:outline-none p-2 text-sm text-white" />
          </label>

          <div className="border border-[#FF4500]/40 bg-[#FF4500]/5 p-4 text-xs flex items-start gap-2">
            <ShieldCheck className="w-4 h-4 text-[#FF4500] mt-0.5 shrink-0" />
            <span className="text-[#E5E5E5]">
              Next step: a paid on-court assessment with a coach (price set per coach — may also be included as part of a packaged coaching deal). Performance and 1:1 sessions stay locked until your assessment is complete. Group sessions open as soon as you finish this rating.
            </span>
          </div>

          <button
            type="submit" disabled={saving || !selected}
            className={`w-full py-4 text-sm inline-flex items-center justify-center gap-2 transition-all ${selected ? "btn-volt" : "cursor-not-allowed"}`}
            style={selected ? { background: "#FF4500", color: "#0A0A0A" } : { background: "#262626", color: "#525252" }}
            data-testid="self-rating-submit">
            {saving ? "Saving…" : (<>Save &amp; book assessment <ArrowRight className="w-4 h-4" /></>)}
          </button>
        </form>
      </div>
    </div>
  );
}
